/**
 * Impresión (bloque 11 del plan de entrega, 2026-10-09). La factura, la nota de crédito y la nota de débito se imprimen
 * SIEMPRE sobre el papel PREIMPRESO de la imprenta (cabecera, logo y número de control ya vienen en la hoja; una forma
 * libre no es válida de otro modo): el sistema imprime solo el cuerpo, en el lugar que marca la plantilla de la sucursal. Los márgenes se calibran en milímetros con una hoja
 * de prueba. El despacho sale en media carta vertical o en tiquera de 80 mm, según la caja.
 */
import { z } from 'zod';

/**
 * Papel de la factura. `media_carta` es la media carta HORIZONTAL de los talonarios viejos de Barinas (8,5 × 5,5 in).
 * `media_carta_vertical` es la misma factura en media carta, impresa en la MITAD DE ARRIBA de una hoja carta que entra
 * de pie en la impresora y después se corta (fotos del usuario del 2026-11-03: con la página acostada, la impresora la
 * giraba sobre la hoja de pie). El nombre de la clave quedó de la primera versión de ese día (una media carta de pie,
 * 5,5 × 8,5 in), que no era lo que se pedía.
 */
export const FORMATOS_PAPEL = ['carta', 'media_carta', 'media_carta_vertical'] as const;
export type FormatoPapel = (typeof FORMATOS_PAPEL)[number];

/** Nombre de cada papel en las pantallas y en la hoja de prueba. */
export const PAPEL_ETIQUETA: Record<FormatoPapel, string> = {
  carta: 'Carta (8,5 × 11 in)',
  media_carta: 'Media carta horizontal (8,5 × 5,5 in)',
  media_carta_vertical: 'Media carta en hoja carta de pie (la mitad de arriba; se corta)',
};

/** matriz = letra de ancho fijo para la Epson LX-300+II · laser = letra proporcional (HP LaserJet). */
export const FUENTES_FACTURA = ['matriz', 'laser'] as const;
export type FuenteFactura = (typeof FUENTES_FACTURA)[number];

const mm = (max: number) => z.number().min(0).max(max).multipleOf(0.5);

export const plantillaFacturaInput = z.object({
  /** mm desde el borde superior de la hoja hasta la primera línea que imprime el sistema (alto de la cabecera preimpresa). */
  superior_mm: mm(150),
  /** mm desde el borde izquierdo de la hoja hasta el texto. */
  izquierdo_mm: mm(40),
  /** mm desde el borde derecho de la hoja hasta el final de los totales. */
  derecho_mm: mm(40),
  /** mm desde el borde inferior de la hoja hasta el final de los totales (alto del pie de imprenta). */
  inferior_mm: mm(100),
  /** Renglones de producto que caben en una hoja. Con más, la factura sigue en otra hoja. */
  renglones: z.number().int().min(1).max(60),
  fuente: z.enum(FUENTES_FACTURA),
});
export type PlantillaFactura = z.infer<typeof plantillaFacturaInput>;

/**
 * Medidas de partida, tomadas de las facturas que envió la clienta (carta de Barquisimeto y media carta horizontal de
 * Barinas). Cada tienda las afina con su hoja de prueba.
 */
export const PLANTILLA_FACTURA_DEFECTO: Record<FormatoPapel, PlantillaFactura> = {
  // Carta: 4 mm a la izquierda y 20 a la derecha (orden del usuario del 2026-11-02, con la foto de una factura de
  // Barquisimeto cortada a la derecha; antes 8 y 12, y antes del 28-10, 6 y 8). En esa impresora la letra sale unos 2 mm
  // corrida a la derecha y algo estirada a lo ancho: con 8 y 12 el último carácter de la columna TOTAL y del número caía
  // fuera del papel. Todo se corre 4 mm a la izquierda y el borde derecho de la letra queda 8 mm más adentro. Arriba,
  // abajo, la letra y los renglones no cambian; las columnas de montos tampoco. La descripción pasa de 38 a 36 letras y
  // el nombre del cliente de 48 a 47 con la letra normal y de 66 a 64 achicando
  carta: { superior_mm: 45, izquierdo_mm: 4, derecho_mm: 20, inferior_mm: 32, renglones: 36, fuente: 'matriz' },
  media_carta: { superior_mm: 39, izquierdo_mm: 6, derecho_mm: 8, inferior_mm: 14, renglones: 9, fuente: 'matriz' },
  // Media carta en la mitad de arriba de una hoja carta de pie (2026-11-03): las mismas medidas de la media carta
  // horizontal (la foto del usuario de cómo debe salir es esa factura); el inferior se mide desde la mitad de la hoja
  media_carta_vertical: { superior_mm: 39, izquierdo_mm: 6, derecho_mm: 8, inferior_mm: 14, renglones: 9, fuente: 'matriz' },
};

/**
 * Plantilla efectiva de una sucursal: lo guardado sobre las medidas de partida de su papel. Cada campo se valida por
 * separado: uno inválido (o desconocido) se ignora y vale el de partida, sin descartar el resto.
 */
export function plantillaFactura(formato: FormatoPapel, guardada: unknown): PlantillaFactura {
  const base = PLANTILLA_FACTURA_DEFECTO[formato] ?? PLANTILLA_FACTURA_DEFECTO.carta;
  if (!guardada || typeof guardada !== 'object' || Array.isArray(guardada)) return base;
  const campos = plantillaFacturaInput.shape;
  const limpio: Record<string, unknown> = {};
  for (const clave of Object.keys(campos) as (keyof typeof campos)[]) {
    const r = campos[clave].safeParse((guardada as Record<string, unknown>)[clave]);
    if (r.success) limpio[clave] = r.data;
  }
  return { ...base, ...(limpio as Partial<PlantillaFactura>) };
}

/** Lugar de la factura en mm: el papel (o, en `media_carta_vertical`, la mitad de arriba de la hoja carta). */
export const PAPEL_MM: Record<FormatoPapel, { ancho: number; alto: number }> = {
  carta: { ancho: 215.9, alto: 279.4 },
  media_carta: { ancho: 215.9, alto: 139.7 },
  media_carta_vertical: { ancho: 215.9, alto: 139.7 },
};

/** Hoja que se le manda a la impresora (`@page`): en `media_carta_vertical`, la hoja carta entera de pie. */
export const HOJA_MM: Record<FormatoPapel, { ancho: number; alto: number }> = {
  carta: PAPEL_MM.carta,
  media_carta: PAPEL_MM.media_carta,
  media_carta_vertical: PAPEL_MM.carta,
};

/** Formato del despacho (entrega de productos), por caja. */
export const FORMATOS_DESPACHO = ['media_carta', 'ticket_80'] as const;
export type FormatoDespacho = (typeof FORMATOS_DESPACHO)[number];
export const FORMATO_DESPACHO_ETIQUETA: Record<FormatoDespacho, string> = { media_carta: 'Media carta vertical', ticket_80: 'Tiquera de 80 mm' };

/** Leyendas de los formatos: texto que la dueña edita en Configuración (`leyenda_<clave>`; vacío = no se imprime). */
export const LEYENDAS_FORMATO = ['cambios', 'cortes', 'retiro', 'igtf', 'precios', 'factura_igtf'] as const;
export type LeyendaFormato = (typeof LEYENDAS_FORMATO)[number];

/** Una tienda en el membrete de los formatos: su nombre, su teléfono fijo y su WhatsApp. */
export interface TiendaImpresion {
  nombre: string;
  telefono: string | null;
  whatsapp: string | null;
}

/** Datos de la empresa para la cabecera de los formatos (`GET /sucursales/empresa`). */
export interface EmpresaImpresionDTO {
  razon_social: string;
  rif: string;
  direccion_fiscal: string;
  telefono: string | null;
  correo: string | null;
  /** Sucursal de la sesión (null para la dueña fuera de una tienda). */
  sucursal: {
    nombre: string;
    direccion_fiscal: string;
    telefono: string | null;
    formato_impresion: FormatoPapel;
    /**
     * Medidas del papel de factura de la sucursal (2026-11-03): en la mitad de una hoja carta, el despacho usa sus
     * márgenes de los lados, como la factura. El servidor la manda siempre; opcional para las hojas de las pruebas.
     */
    plantilla_factura?: PlantillaFactura;
  } | null;
  /**
   * Tiendas activas, en el orden en que se crearon, con su teléfono fijo y su WhatsApp: el membrete las nombra a todas
   * (pedido de la dueña 2026-10-19). Si ninguna tiene teléfono se usa el de la empresa. El servidor lo manda siempre;
   * es opcional solo para las hojas que las pruebas arman a mano.
   */
  tiendas?: TiendaImpresion[];
  leyendas: Record<LeyendaFormato, string>;
  /** IGTF vigente (%), para el renglón del IGTF de la factura. */
  igtf_pct: string;
  /** Formato del despacho de la caja que el usuario tiene abierta (media carta si no tiene caja). */
  despacho_formato: FormatoDespacho;
}

/** Tamaño máximo de la firma ya decodificada (la pantalla la reduce antes de enviarla). */
export const FIRMA_MAX_BYTES = 300 * 1024;
/**
 * Firma que se imprime en los comprobantes de retención: imagen PNG o JPEG como data URL, o null para quitarla.
 * Vive en la base (no es un archivo público) y solo la recibe quien puede ver compras.
 */
export const firmaRetencionesInput = z.object({
  imagen: z
    .string()
    .max(Math.ceil((FIRMA_MAX_BYTES * 4) / 3) + 64, 'La imagen es demasiado grande')
    .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+=*$/, 'Debe ser una imagen PNG o JPEG')
    .nullable(),
});
export type FirmaRetencionesInput = z.infer<typeof firmaRetencionesInput>;
